import React, { useMemo } from 'react';

type FullscreenData =
  | {
      supported: true;
      request: (element: Element) => Promise<void>;
      exit: () => Promise<void>;
      getActiveElement: () => Element | null;
      isActive: () => boolean;
      eventName: string;
    }
  | {
      supported: false;
      request: null;
      exit: null;
      getActiveElement: () => null;
      isActive: () => false;
      eventName: null;
    };

/**
 * get methods and support flag for native fullscreen
 */
const getFullscreenData = (): FullscreenData => {
  // modern browsers
  if (document.fullscreenEnabled) {
    return {
      supported: true,
      request: (element: Element) => element.requestFullscreen(),
      exit: () => document.exitFullscreen(),
      getActiveElement: () => document.fullscreenElement,
      isActive: () => document.fullscreenElement !== null,
      eventName: 'fullscreenchange',
    };
  }

  // Can add older browser here if support is needed

  return {
    supported: false,
    request: null,
    exit: null,
    getActiveElement: () => null,
    isActive: () => false,
    eventName: null,
  };
};

type NativeFullscreenState =
  | {
      isSupported: boolean;
      isActive: boolean;
      getActiveElement: () => Element | null;
      request: (element: Element) => Promise<void>;
      exit: () => Promise<void>;
    }
  | {
      isSupported: false;
      isActive: false;
      getActiveElement: null;
      request: null;
      exit: null;
    };

/**
 * Hook to detect if native fullscreen is supported and active
 */
export const useNativeFullscreen = (): NativeFullscreenState => {
  const fs = useMemo(() => getFullscreenData(), []);
  const [activeState, setActiveState] = React.useState<boolean>(
    fs.supported ? () => fs.isActive() : false,
  );

  React.useEffect(() => {
    if (!fs.supported) return;

    const handleFullscreenChange = () => setActiveState(fs.isActive());

    document.addEventListener(fs.eventName, handleFullscreenChange);
    return () =>
      document.removeEventListener(fs.eventName, handleFullscreenChange);
  }, [fs]);

  if (!fs.supported) {
    return {
      isSupported: false,
      isActive: false,
      getActiveElement: null,
      request: null,
      exit: null,
    };
  }

  return {
    isSupported: fs.supported,
    isActive: activeState,
    getActiveElement: fs.getActiveElement,
    request: fs.request,
    exit: fs.exit,
  };
};
